import { useState, useCallback } from 'react';
import { message } from 'antd';
import type { Product as ProductType } from '@wbstats/shared';
import { createApiUrl } from '../config/api';

/**
 * Custom hook for managing product operations (fetch, create, delete)
 *
 * @example Basic Usage:
 * ```tsx
 * import { useProductOperations } from '../hooks';
 *
 * function ProductPage() {
 *   const { currentUser } = useSelector((state: RootState) => state.user);
 *   const token = localStorage.getItem('token');
 *
 *   const {
 *     products,
 *     isLoading,
 *     isCreating,
 *     isDeleting,
 *     selectedProducts,
 *     fetchProducts,
 *     createProduct,
 *     deleteProducts,
 *     setSelectedProducts,
 *     clearSelection
 *   } = useProductOperations(token, {
 *     showSuccessMessages: true,
 *     showErrorMessages: true,
 *   });
 *
 *   // Fetch products on mount
 *   useEffect(() => {
 *     if (currentUser?.wb_api_token) {
 *       fetchProducts();
 *     }
 *   }, [fetchProducts, currentUser?.wb_api_token]);
 *
 *   // Handle form submission
 *   const handleSubmit = async (values: CreateProductForm) => {
 *     const success = await createProduct(values);
 *     if (success) {
 *       form.resetFields();
 *     }
 *   };
 *
 *   // Handle bulk delete
 *   const handleBulkDelete = async () => {
 *     if (selectedProducts.length > 0) {
 *       await deleteProducts(selectedProducts);
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       <Table
 *         dataSource={products}
 *         loading={isLoading}
 *         rowSelection={{
 *           selectedRowKeys: selectedProducts,
 *           onChange: (keys) => setSelectedProducts(keys as number[])
 *         }}
 *       />
 *       <Button
 *         onClick={handleBulkDelete}
 *         loading={isDeleting}
 *         disabled={selectedProducts.length === 0}
 *       >
 *         Delete Selected
 *       </Button>
 *     </div>
 *   );
 * }
 * ```
 *
 * @example Advanced Usage with Custom Handlers:
 * ```tsx
 * const {
 *   products,
 *   createProduct,
 * } = useProductOperations(token, {
 *   showSuccessMessages: false, // Handle success manually
 *   onSuccess: {
 *     create: (product) => {
 *       notification.success({
 *         message: 'Product Created',
 *         description: `${product.name} has been created successfully!`
 *       });
 *     },
 *     delete: (deletedIds) => {
 *       console.log(`Deleted products: ${deletedIds.join(', ')}`);
 *     }
 *   },
 *   onError: {
 *     create: (error) => {
 *       console.error('Failed to create product:', error);
 *       // Custom error handling
 *     }
 *   }
 * });
 */

/**
 * Interface for the product creation form data
 */
export interface CreateProductForm {
  productId: number;
  vendor_number: string;
  name: string;
  description?: string;
}

/**
 * Interface for API response when fetching products
 */
interface ProductsApiResponse {
  success: boolean;
  data: ProductType[];
  count: number;
}

/**
 * Interface for API response when creating a product
 */
interface CreateProductApiResponse {
  success: boolean;
  data: ProductType;
  message: string;
}

/**
 * Interface for API response when deleting products
 */
interface DeleteProductsApiResponse {
  success: boolean;
  message: string;
  deletedCount: number;
  deletedProductIds: number[];
}

/**
 * Configuration options for the useProductOperations hook
 */
export interface UseProductOperationsOptions {
  /** Base API URL for product operations */
  apiUrl?: string;
  /** Whether to show success messages for operations */
  showSuccessMessages?: boolean;
  /** Whether to show error messages for operations */
  showErrorMessages?: boolean;
  /** Custom success message handlers */
  onSuccess?: {
    create?: (product: ProductType) => void;
    delete?: (deletedIds: number[]) => void;
    fetch?: (products: ProductType[]) => void;
  };
  /** Custom error message handlers */
  onError?: {
    create?: (error: Error) => void;
    delete?: (error: Error) => void;
    fetch?: (error: Error) => void;
  };
}

/**
 * Return type for the useProductOperations hook
 */
export interface UseProductOperationsReturn {
  // State
  products: ProductType[];
  isLoading: boolean;
  isCreating: boolean;
  isDeleting: boolean;
  selectedProducts: number[];

  // Actions
  fetchProducts: () => Promise<void>;
  createProduct: (productData: CreateProductForm) => Promise<boolean>;
  deleteProducts: (productIds: number[]) => Promise<boolean>;
  setSelectedProducts: (productIds: number[]) => void;

  // Utility
  refetch: () => Promise<void>;
  clearSelection: () => void;
}

/**
 * Custom hook for managing product operations (fetch, create, delete)
 *
 * @param userToken - Authentication token for API requests
 * @param options - Configuration options for the hook
 *
 * @example
 * ```tsx
 * const {
 *   products,
 *   isLoading,
 *   fetchProducts,
 *   createProduct,
 *   deleteProducts
 * } = useProductOperations(userToken);
 *
 * // Fetch products
 * useEffect(() => {
 *   fetchProducts();
 * }, [fetchProducts]);
 *
 * // Create a product
 * const handleSubmit = async (values) => {
 *   const success = await createProduct(values);
 *   if (success) {
 *     // Handle success
 *   }
 * };
 * ```
 */
export const useProductOperations = (
  userToken?: string,
  options: UseProductOperationsOptions = {}
): UseProductOperationsReturn => {
  const {
    apiUrl = createApiUrl('products'),
    showSuccessMessages = true,
    showErrorMessages = true,
    onSuccess,
    onError,
  } = options;

  // State management
  const [products, setProducts] = useState<ProductType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);

  /**
   * Fetches products from the API
   */
  const fetchProducts = useCallback(async (): Promise<void> => {
    if (!userToken) {
      console.warn('No user token provided for fetching products');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${userToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch products: ${response.status} ${response.statusText}`);
      }

      const responseData = (await response.json()) as ProductsApiResponse | ProductType[];

      // Handle both old and new API response formats
      const data = Array.isArray(responseData) ? responseData : responseData.data;

      setProducts(data);

      if (showSuccessMessages && onSuccess?.fetch) {
        onSuccess.fetch(data);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load products';
      console.error('Fetch products error:', error);

      if (showErrorMessages) {
        void message.error(errorMessage);
      }

      if (onError?.fetch) {
        onError.fetch(error instanceof Error ? error : new Error(errorMessage));
      }
    } finally {
      setIsLoading(false);
    }
  }, [userToken, apiUrl, showSuccessMessages, showErrorMessages, onSuccess, onError]);

  /**
   * Creates a new product
   */
  const createProduct = useCallback(
    async (productData: CreateProductForm): Promise<boolean> => {
      if (!userToken) {
        console.warn('No user token provided for creating product');
        return false;
      }

      setIsCreating(true);
      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userToken}`,
          },
          body: JSON.stringify(productData),
        });

        const responseData = (await response.json()) as
          | CreateProductApiResponse
          | { error: string };

        if (!response.ok) {
          const errorMsg =
            'error' in responseData
              ? responseData.error
              : `Failed to create product: ${response.status}`;
          throw new Error(errorMsg);
        }

        const result = responseData as CreateProductApiResponse;

        // Add the new product to the list
        setProducts((prev) => [result.data, ...prev]);

        if (showSuccessMessages) {
          void message.success(result.message || 'Product created successfully');
        }

        if (onSuccess?.create) {
          onSuccess.create(result.data);
        }

        return true;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to create product';
        console.error('Create product error:', error);

        if (showErrorMessages) {
          void message.error(errorMessage);
        }

        if (onError?.create) {
          onError.create(error instanceof Error ? error : new Error(errorMessage));
        }

        return false;
      } finally {
        setIsCreating(false);
      }
    },
    [userToken, apiUrl, showSuccessMessages, showErrorMessages, onSuccess, onError]
  );

  /**
   * Deletes multiple products
   */
  const deleteProducts = useCallback(
    async (productIds: number[]): Promise<boolean> => {
      if (!userToken) {
        console.warn('No user token provided for deleting products');
        return false;
      }

      if (productIds.length === 0) {
        if (showErrorMessages) {
          void message.warning('No products selected for deletion');
        }
        return false;
      }

      setIsDeleting(true);
      try {
        const response = await fetch(apiUrl, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userToken}`,
          },
          body: JSON.stringify({ productIds }),
        });

        const responseData = (await response.json()) as
          | DeleteProductsApiResponse
          | { error: string };

        if (!response.ok) {
          const errorMsg =
            'error' in responseData
              ? responseData.error
              : `Failed to delete products: ${response.status}`;
          throw new Error(errorMsg);
        }

        const result = responseData as DeleteProductsApiResponse;

        // Remove deleted products from the list
        setProducts((prev) => prev.filter((product) => !productIds.includes(product.productId)));

        // Clear selection
        setSelectedProducts([]);

        if (showSuccessMessages) {
          void message.success(
            result.message || `${result.deletedCount} product(s) deleted successfully`
          );
        }

        if (onSuccess?.delete) {
          onSuccess.delete(result.deletedProductIds);
        }

        return true;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to delete products';
        console.error('Delete products error:', error);

        if (showErrorMessages) {
          void message.error(errorMessage);
        }

        if (onError?.delete) {
          onError.delete(error instanceof Error ? error : new Error(errorMessage));
        }

        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [userToken, apiUrl, showSuccessMessages, showErrorMessages, onSuccess, onError]
  );

  /**
   * Clears the current product selection
   */
  const clearSelection = useCallback((): void => {
    setSelectedProducts([]);
  }, []);

  /**
   * Refetches products (alias for fetchProducts)
   */
  const refetch = useCallback((): Promise<void> => {
    return fetchProducts();
  }, [fetchProducts]);

  return {
    // State
    products,
    isLoading,
    isCreating,
    isDeleting,
    selectedProducts,

    // Actions
    fetchProducts,
    createProduct,
    deleteProducts,
    setSelectedProducts,

    // Utility
    refetch,
    clearSelection,
  };
};
